import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import type {IJSONProvider} from "data/providers/json/json.provider";
import {Bindings} from "data/constants/bindings";
import {Handedness} from "data/enums";

export interface IContestPlayer {
	playerId: number;
	squadId: number;
	name: string;
	firstName: string;
	lastName: string;
	position: string;
	winProbability: number | null;
	isFeatured: boolean;
	selectedStats: string[] | null;
	stats: IStats;
	status: string;
	totalStats: string[];
	feedId: string;
	handedness: Handedness;
}

interface IStats {
	hitting: IHitting;
	pitching: IPitching;
}

interface IHitting {
	atBats: number;
	hits: number;
	runs: number;
	rbi: number;
	walk: number;
	strikeout: number;
	sacFlies: number;
	double: number;
	triple: number;
	homeRun: number;
	hitByPitch: number;
	caughtStealing: number;
	stolenBases: number;
	leftOnBase: number;
	gidp: number;
	summary: string | null;
}
interface IPitching {
	inningsPitched: number;
	hits: number;
	runs: number;
	earnedRuns: number;
	baseOnBalls: number;
	strikeout: number;
	homeRuns: number;
	era: number;
	summary: string | null;
}

export interface IContestPlayersStore {
	get getIsLoading(): boolean;
	get list(): IContestPlayer[];
	getPlayerById(playerId: number): IContestPlayer | undefined;
	fetchPlayers(id: number): Promise<void>;
}

@injectable()
export class ContestPlayersStore implements IContestPlayersStore {
	@observable private _isLoading: boolean = false;

	constructor(@inject(Bindings.JSONProvider) private _jsonProvider: IJSONProvider) {
		makeAutoObservable(this);
	}

	@observable private _list: IContestPlayer[] = [];

	get list() {
		return this._list;
	}

	get getIsLoading(): boolean {
		return this._isLoading;
	}

	getPlayerById(playerId: number): IContestPlayer | undefined {
		return this._list.find((contest) => contest.playerId === playerId);
	}

	@action
	async fetchPlayers(id: number) {
		this._isLoading = true;
		const {data} = await this._jsonProvider.contest_players(id);

		runInAction(() => {
			this._list = data.players;
			this._isLoading = false;
		});
	}
}
